import React, { useContext, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Avatar,
  Checkbox,
  Modal,
  MultiSelect,
  Select,
  TextInput,
} from '@mantine/core';
import { AuthContext } from 'context/authContext';
import PrimaryButton from 'components/primaryButton';

import useGetEmployeesList from '../../myProjects/hooks/useGetEmployeesList';
import useGetListOfProjects from '../hooks/useGetListOfProjects';
import useAddTaskBoardChannel from '../hooks/useAddTaskBoardChannel';
import useUpdateTaskBoard from '../hooks/useUpdateTaskBoardChannel';
import useGetAllTaskboardMembers from '../hooks/useGetAllTaskboardMembers';
import useAddTaskboardMembers from '../hooks/useAddTaskBoardMember';
import useDeleteTaskboardMember from '../hooks/useDeleteTaskboardMember';

import { ReactComponent as CrossIcon } from 'assets/icons/crossIcon.svg';
import { ReactComponent as DownArrow } from 'assets/icons/downArrow.svg';

export default function CreateChannel({
  isModalOpen,
  setIsModalOpen,
  taskboardDetails,
  name,
}) {
  const { authState } = useContext(AuthContext);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const defaultValues = {
    name: '',
    members: '',
    for_project: false,
    created_by_id: authState?.user_id,
  };

  const {
    handleSubmit,
    formState: { errors },
    watch,
    control,
    reset,
    // setValue,
  } = useForm({ defaultValues, mode: 'onChange' });

  const getEmployesList = useGetEmployeesList();
  const getListOfProjects = useGetListOfProjects();
  const addTaskBoardMutation = useAddTaskBoardChannel(closeModal, reset);
  const updateTaskboardChannelMutation = useUpdateTaskBoard(closeModal, true);
  const getAllTaskBoardMember = useGetAllTaskboardMembers(taskboardDetails?.id);
  const addTaskboardMemberMutation = useAddTaskboardMembers();
  const deleteTaskboardMember = useDeleteTaskboardMember();

  //   error message
  const getErrorMessage = (name) => {
    return (
      errors[name] && (
        <p className=' text-sm font-semibold text-red-500'>
          {errors[name]?.message}
        </p>
      )
    );
  };

  const onSubmit = (data) => {
    if (data.for_project) {
      delete data?.for_project;
    } else {
      delete data?.for_project;
      data.project_id = null;
    }

    if (data?.id) {
      delete data?.members;
      updateTaskboardChannelMutation.mutate(data);
    } else {
      addTaskBoardMutation?.mutate(data);
    }
  };

  //   custom dropdown item for select
  const SelectItem = ({
    value,
    label,
    image,
    name,
    description,
    ...others
  }) => {
    return (
      <div {...others} className='flex items-center py-2 cursor-pointer'>
        <Avatar src={image} color='red'>
          {label[0]?.toUpperCase()}
        </Avatar>
        <div className='pl-1'>
          <p className='text-18'>{label}</p>
          <p className='text-xs text-gray3 pt-0.5'>{description}</p>
        </div>
      </div>
    );
  };

  // update taskboard member data
  const changeHandler = (data) => {
    if (getAllTaskBoardMember?.data?.data?.length > data?.length) {
      const removedEmployee = getAllTaskBoardMember?.data?.data?.filter(
        (member) => data.indexOf(`${member?.employee_id}`)
      );
      deleteTaskboardMember?.mutate({ id: removedEmployee[0]?.id });
    } else {
      if (data?.length > getAllTaskBoardMember?.data?.data?.length) {
        addTaskboardMemberMutation.mutate({
          task_board_id: taskboardDetails?.id,
          employee_id: data[data?.length - 1],
        });
      }
    }
  };

  useEffect(() => {
    if (taskboardDetails) {
      const membersData = getAllTaskBoardMember?.data?.data?.map(
        (item) => `${item?.employee_id}`
      );
      reset({
        id: taskboardDetails?.id,
        name: taskboardDetails?.name,
        for_project: taskboardDetails?.project ? true : false,
        members: membersData,
        project_id: taskboardDetails?.project
          ? `${taskboardDetails?.project}`
          : null,
      });
    } else {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskboardDetails, getAllTaskBoardMember?.data?.data]);

  const getProjectDropdownValues = () => {
    let finalArray = [];

    getListOfProjects?.data?.data?.map((item) => {
      if (item.is_active) {
        // checking for inactive projects
        finalArray.push({
          value: `${item?.id}`,
          label: item?.title,
        });
      }

      return true;
    });
    // console.log('finalArray', finalArray);
    // if (taskboardDetails?.project) {
    //   finalArray = [
    //     ...finalArray,
    //     {
    //       value: `${taskboardDetails?.project}`,
    //       label: taskboardDetails?.project__title,
    //     },
    //   ];
    // }
    return finalArray;
  };

  return (
    <div>
      <Modal
        opened={isModalOpen}
        onClose={() => closeModal(false)}
        title={''}
        withCloseButton={false}
        overlayColor={'#F8F7F799'}
        overlayBlur={20}
        overlayOpacity={0.5}
        centered
        closeOnClickOutside={false}
        size='lg'
        radius='md'
        overflow='inside'
        transitionDuration={100}
        classNames={{
          title: 'w-full text-333333 font-semibold text-xl text-center',
          modal: 'pl-10',
          body: 'scroller -mr-4 pr-2',
        }}
      >
        <button className='hidden'></button>
        <CrossIcon
          className='w-10 ml-auto cursor-pointer'
          onClick={() => closeModal()}
        />
        <p className='text-xl font-bold text-gray text-center pt-2'>
          {name} channel
        </p>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='w-full py-2 pr-4'>
            <Controller
              control={control}
              name='name'
              rules={{
                required: 'required',
                validate: () =>
                  watch('name').length < 249 ||
                  'maximum length exceded (250 chars)',
              }}
              render={({ field }) => (
                <TextInput
                  label='Name of the channel*'
                  {...field}
                  size='md'
                  classNames={{
                    input: 'border-primary h-12',
                    label: 'text-neutralsGrey text-sm',
                  }}
                />
              )}
            />
            {getErrorMessage('name')}
          </div>
          {name === 'Edit' ? (
            <div className='w-full py-2 pr-4'>
              <Controller
                control={control}
                name='members'
                rules={{ required: 'required' }}
                render={({ field }) => (
                  <MultiSelect
                    {...field}
                    size='md'
                    itemComponent={SelectItem}
                    label='Assign members*'
                    placeholder='Search or select'
                    searchable
                    onChange={(data) => changeHandler(data)}
                    data={
                      getEmployesList?.data?.data?.length
                        ? getEmployesList?.data?.data
                            ?.filter((element) => element?.user__is_active)
                            ?.map((item) => {
                              return {
                                value: `${item?.id}`,
                                label: item?.user__first_name,
                                image: item?.profile_picture,
                                description: item?.designation__name,
                              };
                            })
                        : []
                    }
                    rightSection={<DownArrow />}
                    styles={{ rightSection: { pointerEvents: 'none' } }}
                    classNames={{
                      input: 'border-primary truncate min-h-[7vh]',
                      label: 'text-neutralsGrey text-sm',
                    }}
                  />
                )}
              />
              {getErrorMessage('members')}
            </div>
          ) : (
            <div className='w-full py-2 pr-4'>
              <Controller
                control={control}
                name='members'
                rules={{ required: 'required' }}
                render={({ field }) => (
                  <MultiSelect
                    {...field}
                    size='md'
                    itemComponent={SelectItem}
                    label='Assign members*'
                    placeholder='Search or select'
                    searchable
                    //   onChange={(data) => changeHandler(data)}
                    data={
                      getEmployesList?.data?.data?.length
                        ? getEmployesList?.data?.data
                            ?.filter((element) => element?.user__is_active)
                            ?.map((item) => {
                              return {
                                value: `${item?.id}`,
                                label: item?.user__first_name,
                              };
                            })
                        : []
                    }
                    rightSection={<DownArrow />}
                    styles={{ rightSection: { pointerEvents: 'none' } }}
                    classNames={{
                      input: 'border-primary truncate min-h-[7vh]',
                      label: 'text-neutralsGrey text-sm',
                    }}
                  />
                )}
              />
              {getErrorMessage('members')}
            </div>
          )}
          <div className='mt-4'>
            <Controller
              control={control}
              name='for_project'
              render={({ field }) => (
                <Checkbox
                  {...field}
                  checked={watch('for_project')}
                  label='Channel for a specific project'
                  classNames={{
                    label: 'text-neutralsGrey',
                  }}
                />
              )}
            />
            {watch('for_project') ? (
              <div className='w-1/2'>
                <Controller
                  control={control}
                  name='project_id'
                  rules={{ required: 'required' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      size='md'
                      placeholder='Select project'
                      searchable
                      data={
                        getListOfProjects?.data?.data?.length
                          ? getProjectDropdownValues()
                          : []
                      }
                      rightSection={<DownArrow />}
                      styles={{ rightSection: { pointerEvents: 'none' } }}
                      classNames={{
                        input: 'border-primary truncate min-h-[7vh] mt-4',
                        label: 'text-neutralsGrey text-sm',
                      }}
                    />
                  )}
                />
              </div>
            ) : null}
            <div className='mt-8 mb-2 flex justify-center'>
              <button className='w-32 mx-auto'>
                <PrimaryButton isLoading={addTaskBoardMutation?.isLoading}>
                  Save
                </PrimaryButton>
              </button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}
